import { useCallback } from 'react';
import {
  ListSubheader,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ISearchFilter } from '../../common-types';
import { FilterMenuItem } from './FilterMenuItem';
import { getFilterName, normalizeFilterName } from '../__feature__';

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
}));

const StyledListSubheader = styled(ListSubheader)(({ theme }) => ({
  color: theme.palette.common.black,
}));

const MenuProps = {
  PaperProps: {
    style: { maxHeight: 400 },
  },
};

type GroupedArrayFilterProps = {
  filter: ISearchFilter;
  selectedOptions: string[];
  handleSelectedChange: (selectedOptions: string[]) => void;
  testId?: string;
};

export const GroupedArrayFilter = ({
  filter,
  selectedOptions,
  handleSelectedChange,
  testId,
}: GroupedArrayFilterProps) => {
  const filterName = getFilterName(filter?.filterName);
  const isGroupedArrayFilter = filter.dataType === 'GroupedArray';

  const getRenderValue = useCallback(
    (selected: string[]) => {
      if (isGroupedArrayFilter && filter.isValueTranslationRequired) {
        return selected.map(() => filterName).join(', ');
      }
      return selected.join(', ');
    },
    [filterName]
  );

  const handleChange = useCallback(
    ({ target: { value } }: SelectChangeEvent<string[]>) => {
      handleSelectedChange(
        typeof value === 'string' ? value.split(',') : value
      );
    },
    [handleSelectedChange]
  );

  const renderGroupedOptions = () => {
    return isGroupedArrayFilter
      ? filter?.filterValues
          ?.filter((g) => g.filterValues?.length > 0)
          .sort((a, b) => a.filterName.localeCompare(b.filterName))
          .map((group) => [
            <StyledListSubheader key={group.filterName}>
              {group.filterName}
            </StyledListSubheader>,
            group.filterValues?.map((option) => (
              <MenuItem key={option} value={option}>
                <FilterMenuItem
                  option={option}
                  isChecked={selectedOptions.includes(option)}
                />
              </MenuItem>
            )),
          ])
      : null;
  };

  return (
    <>
      {isGroupedArrayFilter && (
        <StyledFormControl fullWidth size="small" data-testid={testId}>
          <InputLabel id={`filter-label-${normalizeFilterName(filterName)}`}>
            {filterName}
          </InputLabel>
          <Select
            labelId={`filter-label-${normalizeFilterName(filterName)}`}
            multiple
            value={selectedOptions}
            onChange={handleChange}
            label={getFilterName(filter.filterName)}
            renderValue={getRenderValue}
            variant="outlined"
            MenuProps={MenuProps}
          >
            {renderGroupedOptions()}
          </Select>
        </StyledFormControl>
      )}
    </>
  );
};
